// GantMulti.jsx
import React, {
  useRef,
  useEffect,
  useState,
  useContext,
  useMemo,
  useLayoutEffect,
  useCallback,
  memo,
} from 'react';
import styles from './GantMulti.module.css';
import { AppContext } from 'context/AppContext';
import { TechnologyContext } from 'context/TechnologyContext';
import {
  Shrink,
  Spline,
  Waypoints,
  X,
  Layers,
  Minus,
  Plus,
  RotateCcw,
  Sparkles,
  Save,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getNameOfElementTyp } from 'actions/getNameOfElementTyp';
import { date_time } from 'actions/date_time';
import { date_bez_time } from 'actions/date_bez_time';
import { useGanttZoom, GanttZoomButtons, GanttZoomOverlay } from './GanttZoom';
import {
  resolveProcessorOverlaps,
  GantMultiDelayPanel,
  getAllowedProcessorIdsForStage,
  getProcesNazwaId,
} from './GantMultiResolver';
import axios from 'axios';
import { IP } from "utils/Host";

const TIMELINE_HEADER_HEIGHT = 34; // Stała wysokość nagłówka osi czasu (.timeline)
const LANE_HEIGHT = 36;            // Wysokość pojedynczego toru (sub-wiersza) w pikselach
const BAR_HEIGHT = 28;             // Wysokość samego klocka w pikselach
const BAR_OFFSET_Y = (LANE_HEIGHT - BAR_HEIGHT) / 2; // Symetryczny odstęp góra/dół (4px) wewnątrz każdego toru
const ROW_PADDING_Y = 0;           // Brak niesymetrycznego paddingu rozpychającego pierwszy/ostatni tor
const DRAG_THRESHOLD_PX = 5;       // Próg w pikselach odróżniający kliknięcie od przeciągania

const PALETA_KOLOROW = [
  '#3f51b5', '#f4a261', '#e63946', '#2a9d8f', '#9c27b0',
  '#e91e63', '#00bcd4', '#ff9800', '#8bc34a', '#795548',
];

const IGNORED_PROCESSOR_IDS = new Set([51, 52, 53, 54]);

// --- FUNKCJE POMOCNICZE RELACJI I DAT ---

const czyMajaWspolnyArkusz = (arkuszeA, arkuszeB) => {
  if (!arkuszeA || !arkuszeB) return true;

  const listA = Array.isArray(arkuszeA)
    ? arkuszeA.map(String)
    : String(arkuszeA)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

  const listB = Array.isArray(arkuszeB)
    ? arkuszeB.map(String)
    : String(arkuszeB)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

  if (listA.length === 0 || listB.length === 0) return true;

  return listA.some((arkusz) => listB.includes(arkusz));
};

const zawieraElement = (elementyIdText, searchElementId) => {
  if (
    elementyIdText === null ||
    elementyIdText === undefined ||
    searchElementId === null ||
    searchElementId === undefined
  ) {
    return false;
  }
  const target = String(searchElementId).trim();

  if (Array.isArray(elementyIdText)) {
    return elementyIdText.map(String).includes(target);
  }

  const lista = String(elementyIdText)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return lista.includes(target);
};

const formatDateToCustomString = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const getShipmentTimestampMs = (dateVal) => {
  if (!dateVal) return NaN;
  const d = new Date(String(dateVal).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return NaN;
  const str = String(dateVal).trim();
  if (str.length <= 10 || (d.getHours() === 0 && d.getMinutes() === 0)) {
    d.setHours(6, 0, 0, 0);
  }
  return d.getTime();
};

const getStageUid = (stage) => {
  const prefix = getProcesNazwaId(stage) === 6 ? 'OPR' : 'STG';
  return `${prefix}_${stage.global_id ?? stage.id}`;
};

// Mapowanie dla GantMultiResolver, aby operował na polach *_symulacji (z fallbackiem do wejściowych)
const mapStagesForResolver = (stagesList = []) =>
  stagesList.map((s) => ({
    ...s,
    _orig_procesor_id: s.procesor_id,
    _orig_data_spedycji: s.data_spedycji,
    _orig_data_rozpoczecia_produkcji: s.data_rozpoczecia_produkcji,
    procesor_id: s.procesor_id_symulacji ?? s.procesor_id,
    data_spedycji:
      s.spedycja_symulacji || s.data_spedycji_symulacji || s.data_spedycji,
    data_rozpoczecia_produkcji:
      s.data_rozpoczecia_symulacji || s.data_rozpoczecia_produkcji,
  }));

// --- 1. SILNIK WYLICZAJĄCY POŁOŻENIE KLOCKÓW OD SPEDYCJI (-24H) ---

const calculateBackwardStages = (
  stages = [],
  customMargins = {},
  customShipmentDelays = {},
  customProdStartLimits = {},
  customProcessors = {},
  savedBaselineMap = {}
) => {
  if (!Array.isArray(stages) || stages.length === 0) return [];

  const activeStages = stages.filter((s) => s && s.status != 4);
  if (activeStages.length === 0) return [];

  const orderOrigShipmentMap = {};
  const orderSimShipmentMap = {};
  const orderOrigProdStartMap = {};
  const orderSimProdStartMap = {};

  stages.forEach((s) => {
    if (!s || !s.zamowienie_id) return;
    const zamId = String(s.zamowienie_id);
    const uid = getStageUid(s);
    const baseSaved = savedBaselineMap[uid];

    if (s.data_spedycji && !orderOrigShipmentMap[zamId]) {
      orderOrigShipmentMap[zamId] = s.data_spedycji;
    }
    const simSped =
      baseSaved?.spedycja_symulacji ||
      s.spedycja_symulacji ||
      s.data_spedycji_symulacji;
    if (simSped && !orderSimShipmentMap[zamId]) {
      orderSimShipmentMap[zamId] = simSped;
    }
    if (s.data_rozpoczecia_produkcji && !orderOrigProdStartMap[zamId]) {
      orderOrigProdStartMap[zamId] = s.data_rozpoczecia_produkcji;
    }
    const simRozp =
      baseSaved?.data_rozpoczecia_symulacji || s.data_rozpoczecia_symulacji;
    if (simRozp && !orderSimProdStartMap[zamId]) {
      orderSimProdStartMap[zamId] = simRozp;
    }
  });

  const stagesMap = new Map();

  activeStages.forEach((s) => {
    const uid = getStageUid(s);
    const zamId = s.zamowienie_id ? String(s.zamowienie_id) : null;
    const procNazwaId = getProcesNazwaId(s);
    const allowedPool = getAllowedProcessorIdsForStage(s);
    const baseSaved = savedBaselineMap[uid];

    const origStartMs = new Date(s.start).getTime();
    const origEndMs = new Date(s.end).getTime();

    const initSimStartMs = s.poczatek_symulacji
      ? new Date(s.poczatek_symulacji).getTime()
      : origStartMs;
    const initSimEndMs = s.koniec_symulacji
      ? new Date(s.koniec_symulacji).getTime()
      : origEndMs;

    const durationMinutes =
      Number(s.czas) > 0
        ? Number(s.czas)
        : Math.max(1, (origEndMs - origStartMs) / 60000);
    const durationMs = durationMinutes * 60 * 1000;

    const initialMarginLeft = Number(
      baseSaved?.marginLeft ?? s.marginLeft ?? s.marginleft ?? 0
    );
    const initialMarginRight = Number(
      baseSaved?.marginRight ?? s.marginRight ?? s.marginright ?? 0
    );

    const override = customMargins[uid] || {};
    const marginLeftMin = Number(override.marginLeft ?? initialMarginLeft);
    const marginRightMin = Number(override.marginRight ?? initialMarginRight);

    // 1. Procesor symulacji (z walidacją dozwolonej puli procesorów)
    const initialSimProcesorId =
      baseSaved?.procesor_id_symulacji !== undefined
        ? Number(baseSaved.procesor_id_symulacji)
        : s.procesor_id_symulacji !== undefined &&
          s.procesor_id_symulacji !== null &&
          s.procesor_id_symulacji !== ''
        ? Number(s.procesor_id_symulacji)
        : Number(s.procesor_id);

    let candidateSimProcId =
      customProcessors[uid] !== undefined
        ? Number(customProcessors[uid])
        : initialSimProcesorId;

    if (
      String(candidateSimProcId) !== String(s.procesor_id) &&
      !allowedPool.includes(Number(candidateSimProcId))
    ) {
      candidateSimProcId = Number(s.procesor_id);
    }

    // 2. Data spedycji (oryginalna vs symulacyjna z fallbackiem)
    const origSpedycja =
      s.data_spedycji || (zamId ? orderOrigShipmentMap[zamId] : null) || null;
    const initialSimSpedycja =
      baseSaved?.spedycja_symulacji ||
      s.spedycja_symulacji ||
      s.data_spedycji_symulacji ||
      (zamId ? orderSimShipmentMap[zamId] : null) ||
      origSpedycja;

    // 3. Data rozpoczęcia produkcji (oryginalna vs symulacyjna z fallbackiem)
    const origRozpoczecie =
      s.data_rozpoczecia_produkcji ||
      (zamId ? orderOrigProdStartMap[zamId] : null) ||
      null;
    const initialSimRozpoczecie =
      baseSaved?.data_rozpoczecia_symulacji ||
      s.data_rozpoczecia_symulacji ||
      (zamId ? orderSimProdStartMap[zamId] : null) ||
      origRozpoczecie;

    const baseSimRozpoczecie =
      zamId && customProdStartLimits[zamId]
        ? formatDateToCustomString(new Date(customProdStartLimits[zamId]))
        : initialSimRozpoczecie;

    stagesMap.set(uid, {
      ...s,
      start: s.start,
      end: s.end,
      procesor_id: s.procesor_id,
      data_spedycji: origSpedycja,
      data_rozpoczecia_produkcji: origRozpoczecie,
      procesor_id_symulacji: candidateSimProcId,
      spedycja_symulacji: initialSimSpedycja,
      data_spedycji_symulacji: initialSimSpedycja,
      data_rozpoczecia_symulacji: baseSimRozpoczecie,
      marginLeft: marginLeftMin,
      marginRight: marginRightMin,
      // Wartości bazowe do porównania przy zapisie
      _initMarginLeft: initialMarginLeft,
      _initMarginRight: initialMarginRight,
      _initSimProcesorId: initialSimProcesorId,
      _initSimSpedycja: initialSimSpedycja,
      _initSimRozpoczecie: initialSimRozpoczecie,
      _uid: uid,
      _procesNazwaId: procNazwaId,
      _origStartMs: origStartMs,
      _origEndMs: origEndMs,
      _origDurationMs: Math.max(60000, origEndMs - origStartMs),
      _durationMs: durationMs,
      _startMs: Number.isNaN(initSimStartMs) ? origStartMs : initSimStartMs,
      _endMs: Number.isNaN(initSimEndMs) ? origEndMs : initSimEndMs,
    });
  });

  const ordersMap = {};
  Array.from(stagesMap.values()).forEach((stage) => {
    const activeShipmentDate =
      stage.spedycja_symulacji ||
      stage.data_spedycji_symulacji ||
      stage.data_spedycji;

    if (stage.typ_grupy == 1 || !stage.zamowienie_id || !activeShipmentDate) {
      return;
    }

    const zamId = String(stage.zamowienie_id);
    if (!ordersMap[zamId]) {
      ordersMap[zamId] = {
        oprawy: [],
        byElement: {},
        allOrderStages: [],
        baseShipmentDate: activeShipmentDate,
      };
    }

    ordersMap[zamId].allOrderStages.push(stage);

    if (stage._procesNazwaId === 6) {
      ordersMap[zamId].oprawy.push(stage);
    } else if (stage.element_id !== undefined && stage.element_id !== null) {
      const elemId = String(stage.element_id);
      if (!ordersMap[zamId].byElement[elemId]) {
        ordersMap[zamId].byElement[elemId] = [];
      }
      ordersMap[zamId].byElement[elemId].push(stage);
    }
  });

  Object.keys(ordersMap).forEach((zamId) => {
    const { oprawy, byElement, allOrderStages, baseShipmentDate } = ordersMap[zamId];

    const baseShipMs = getShipmentTimestampMs(baseShipmentDate);
    if (Number.isNaN(baseShipMs)) return;

    const extraShipmentDelayMs = Number(customShipmentDelays[zamId] || 0);
    const effectiveShipmentMs = baseShipMs + extraShipmentDelayMs;
    const baseOprawaDeadlineMs = effectiveShipmentMs - 24 * 60 * 60 * 1000;

    const formattedSimShipment =
      extraShipmentDelayMs !== 0
        ? formatDateToCustomString(new Date(effectiveShipmentMs))
        : baseShipmentDate;

    allOrderStages.forEach((st) => {
      st.spedycja_symulacji = formattedSimShipment;
      st.data_spedycji_symulacji = formattedSimShipment;
      st._effectiveShipmentMs = effectiveShipmentMs;
    });

    // KROK A: Ustawienie Opraw (proces_nazwa_id == 6)
    oprawy.forEach((oprawa) => {
      const marginRightMs = oprawa.marginRight * 60 * 1000;
      oprawa._endMs = baseOprawaDeadlineMs - marginRightMs;
      oprawa._startMs = oprawa._endMs - oprawa._durationMs;
    });

    // KROK B: Ustawienie procesów dla każdego element_id od końca (od oprawy w lewo)
    Object.keys(byElement).forEach((elemId) => {
      const elemStages = byElement[elemId];
      if (elemStages.length === 0) return;

      const byIndex = {};
      elemStages.forEach((s) => {
        const idx = Number(s.indeks_procesu ?? s.indeks ?? s.krok ?? 0);
        if (!byIndex[idx]) byIndex[idx] = [];
        byIndex[idx].push(s);
      });

      const sortedIndexes = Object.keys(byIndex)
        .map(Number)
        .sort((a, b) => a - b);

      let matchingOprawy = oprawy.filter((op) =>
        zawieraElement(op.elementy_id, elemId)
      );
      if (matchingOprawy.length === 0 && oprawy.length > 0) {
        matchingOprawy = oprawy;
      }

      for (let i = sortedIndexes.length - 1; i >= 0; i--) {
        const currentGroup = byIndex[sortedIndexes[i]];

        currentGroup.forEach((stage) => {
          const stageArkusze = stage.arkusze_id || stage.arkusz_id || stage.arkusze;
          let directSuccessors = [];

          for (let j = i + 1; j < sortedIndexes.length; j++) {
            const nextGroup = byIndex[sortedIndexes[j]];
            const matchedNext = nextGroup.filter((ns) =>
              czyMajaWspolnyArkusz(
                stageArkusze,
                ns.arkusze_id || ns.arkusz_id || ns.arkusze
              )
            );
            if (matchedNext.length > 0) {
              directSuccessors = matchedNext;
              break;
            }
          }

          if (directSuccessors.length === 0) {
            directSuccessors = matchingOprawy;
          }

          if (directSuccessors.length > 0) {
            const allowedEnds = directSuccessors.map((succ) => {
              const totalGapMinutes = (succ.marginLeft || 0) + (stage.marginRight || 0);
              return succ._startMs - totalGapMinutes * 60 * 1000;
            });

            stage._endMs = Math.min(...allowedEnds);
          } else {
            stage._endMs = baseOprawaDeadlineMs - (stage.marginRight || 0) * 60 * 1000;
          }

          stage._startMs = stage._endMs - stage._durationMs;
        });
      }
    });
  });

  return Array.from(stagesMap.values()).map((stage) => ({
    ...stage,
    start: stage.start,
    end: stage.end,
    procesor_id: stage.procesor_id,
    data_spedycji: stage.data_spedycji,
    data_rozpoczecia_produkcji: stage.data_rozpoczecia_produkcji,
    procesor_id_symulacji: stage.procesor_id_symulacji,
    spedycja_symulacji: stage.spedycja_symulacji,
    data_spedycji_symulacji: stage.data_spedycji_symulacji,
    data_rozpoczecia_symulacji: stage.data_rozpoczecia_symulacji,
    poczatek_symulacji:
      stage.typ_grupy == 1
        ? null
        : formatDateToCustomString(new Date(stage._startMs)),
    koniec_symulacji:
      stage.typ_grupy == 1
        ? null
        : formatDateToCustomString(new Date(stage._endMs)),
  }));
};

// --- 2. ALGORYTM UKŁADANIA TORÓW PROCESORA ---

const packSimulationLanes = (simStagesForProcessor = []) => {
  const simStages = (simStagesForProcessor || []).filter(
    (stage) => stage.typ_grupy != 1
  );

  if (simStages.length === 0) {
    return { packedSimStages: [], simLaneCount: 0 };
  }

  const TOLERANCE_MS = 60 * 1000;

  const sorted = [...simStages].sort(
    (a, b) => a._startMs - b._startMs || a._endMs - b._endMs
  );

  for (let i = 0; i < sorted.length; i++) {
    let overlapsAny = false;
    for (let j = 0; j < sorted.length; j++) {
      if (i === j) continue;
      if (
        sorted[i]._startMs < sorted[j]._endMs - TOLERANCE_MS &&
        sorted[i]._endMs > sorted[j]._startMs + TOLERANCE_MS
      ) {
        overlapsAny = true;
        break;
      }
    }
    sorted[i]._isOverlapping = overlapsAny;
  }

  const lanesEndMs = [];

  const packedSimStages = sorted.map((stage) => {
    let assignedLane = -1;

    for (let laneIdx = 0; laneIdx < lanesEndMs.length; laneIdx++) {
      if (lanesEndMs[laneIdx] <= stage._startMs + TOLERANCE_MS) {
        assignedLane = laneIdx;
        lanesEndMs[laneIdx] = stage._endMs;
        break;
      }
    }

    if (assignedLane === -1) {
      assignedLane = lanesEndMs.length;
      lanesEndMs.push(stage._endMs);
    }

    return {
      ...stage,
      _renderKey: `${stage._uid}_SIM`,
      _isManualTrack: false,
      _laneIndex: assignedLane + 1,
      _renderStartMs: stage._startMs,
      _renderEndMs: stage._endMs,
      _renderDurationMs: stage._durationMs,
    };
  });

  return {
    packedSimStages,
    simLaneCount: Math.max(1, lanesEndMs.length),
  };
};

const buildProcessorLanesWithManualTrack = (
  manualProcessorStages = [],
  simProcessorStages = []
) => {
  const TOLERANCE_MS = 60 * 1000;

  const manualTrackStages = manualProcessorStages.map((stage, idx) => {
    let manualOverlaps = false;

    if (stage.typ_grupy != 1) {
      for (let j = 0; j < manualProcessorStages.length; j++) {
        if (idx === j) continue;
        const other = manualProcessorStages[j];
        if (other.typ_grupy == 1) continue;
        if (
          stage._origStartMs < other._origEndMs - TOLERANCE_MS &&
          stage._origEndMs > other._origStartMs + TOLERANCE_MS
        ) {
          manualOverlaps = true;
          break;
        }
      }
    }

    return {
      ...stage,
      _renderKey: `${stage._uid}_MANUAL`,
      _isManualTrack: true,
      _isOverlapping: manualOverlaps,
      _laneIndex: 0,
      _renderStartMs: stage._origStartMs,
      _renderEndMs: stage._origEndMs,
      _renderDurationMs: stage._origDurationMs,
    };
  });

  const { packedSimStages, simLaneCount } = packSimulationLanes(simProcessorStages);

  return {
    packedStages: [...manualTrackStages, ...packedSimStages],
    laneCount: 1 + simLaneCount,
    simLaneCount,
  };
};

// --- 3. DEDYKOWANY RENDERER POŁĄCZEŃ SVG DLA GANTMULTI ---

const GantMultiConnectionsSvg = memo(
  ({
    allPreparedStages = [],
    selectedZamowienieId,
    selectedStageUid,
    scaleFactor,
    showElementConections,
    showOprawaConnections,
    showSingleConection,
  }) => {
    const { elementLines, oprawaLines, singleLines } = useMemo(() => {
      if (!selectedZamowienieId || !scaleFactor) {
        return { elementLines: [], oprawaLines: [], singleLines: [] };
      }

      const zamIdStr = String(selectedZamowienieId);
      const orderStages = allPreparedStages.filter(
        (s) =>
          s &&
          !s._isManualTrack &&
          s.typ_grupy != 1 &&
          String(s.zamowienie_id) === zamIdStr
      );

      if (orderStages.length === 0) {
        return { elementLines: [], oprawaLines: [], singleLines: [] };
      }

      const withPoints = orderStages.map((s) => {
        const leftPx = s._startMinutes * scaleFactor;
        const widthPx = s._durationMinutes * scaleFactor;
        const y = s._centerY;

        return {
          ...s,
          _elemIdStr:
            s.element_id !== undefined && s.element_id !== null
              ? String(s.element_id)
              : null,
          _indeks: Number(s.indeks_procesu ?? s.indeks ?? s.krok ?? 0),
          _procesNazwaId: getProcesNazwaId(s),
          _arkusze: s.arkusze_id || s.arkusz_id || s.arkusze,
          _inPoint: { x: leftPx, y },
          _outPoint: { x: leftPx + widthPx, y },
        };
      });

      const oprawy = withPoints.filter((s) => s._procesNazwaId === 6);
      const byElement = {};
      const elementColorMap = {};

      withPoints.forEach((s) => {
        if (s._procesNazwaId === 6 || !s._elemIdStr) return;
        if (!(s._elemIdStr in elementColorMap)) {
          const idx = Object.keys(elementColorMap).length;
          elementColorMap[s._elemIdStr] = PALETA_KOLOROW[idx % PALETA_KOLOROW.length];
        }
        if (!byElement[s._elemIdStr]) byElement[s._elemIdStr] = [];
        byElement[s._elemIdStr].push(s);
      });

      const elemLinesResult = [];
      const oprawaLinesResult = [];
      const singleLinesResult = [];

      Object.keys(byElement).forEach((elemId) => {
        const elemStages = byElement[elemId];
        const color = elementColorMap[elemId] || '#38bdf8';

        const byIndex = {};
        elemStages.forEach((s) => {
          if (!byIndex[s._indeks]) byIndex[s._indeks] = [];
          byIndex[s._indeks].push(s);
        });

        const sortedIndexes = Object.keys(byIndex)
          .map(Number)
          .sort((a, b) => a - b);

        for (let i = 0; i < sortedIndexes.length - 1; i++) {
          const currentGroup = byIndex[sortedIndexes[i]];

          currentGroup.forEach((fromStage) => {
            for (let j = i + 1; j < sortedIndexes.length; j++) {
              const candidateGroup = byIndex[sortedIndexes[j]];
              const matchedNext = candidateGroup.filter((toStage) =>
                czyMajaWspolnyArkusz(fromStage._arkusze, toStage._arkusze)
              );

              if (matchedNext.length > 0) {
                matchedNext.forEach((toStage) => {
                  const lineObj = {
                    id: `el_${fromStage._uid}_to_${toStage._uid}`,
                    from: fromStage._outPoint,
                    to: toStage._inPoint,
                    color,
                  };
                  if (showElementConections) {
                    elemLinesResult.push(lineObj);
                  }
                  if (
                    showSingleConection &&
                    selectedStageUid &&
                    (fromStage._uid === selectedStageUid || toStage._uid === selectedStageUid)
                  ) {
                    singleLinesResult.push(lineObj);
                  }
                });
                break;
              }
            }
          });
        }

        if (sortedIndexes.length > 0 && oprawy.length > 0) {
          const maxIndeks = sortedIndexes[sortedIndexes.length - 1];
          const lastStages = byIndex[maxIndeks] || [];

          let matchingOprawy = oprawy.filter((op) =>
            zawieraElement(op.elementy_id, elemId)
          );
          if (matchingOprawy.length === 0) {
            matchingOprawy = oprawy;
          }

          lastStages.forEach((lastStage) => {
            matchingOprawy.forEach((opStage) => {
              const lineObj = {
                id: `opr_${lastStage._uid}_to_${opStage._uid}`,
                from: lastStage._outPoint,
                to: opStage._inPoint,
                color,
              };
              if (showOprawaConnections) {
                oprawaLinesResult.push(lineObj);
              }
              if (
                showSingleConection &&
                selectedStageUid &&
                (lastStage._uid === selectedStageUid || opStage._uid === selectedStageUid)
              ) {
                singleLinesResult.push(lineObj);
              }
            });
          });
        }
      });

      return {
        elementLines: elemLinesResult,
        oprawaLines: oprawaLinesResult,
        singleLines: singleLinesResult,
      };
    }, [
      allPreparedStages,
      selectedZamowienieId,
      selectedStageUid,
      scaleFactor,
      showElementConections,
      showOprawaConnections,
      showSingleConection,
    ]);

    if (
      elementLines.length === 0 &&
      oprawaLines.length === 0 &&
      singleLines.length === 0
    ) {
      return null;
    }

    const renderCurve = (line, isDashed = false, strokeWidth = 1.5) => {
      const { from, to, id, color } = line;
      const deltaX = Math.max(20, Math.abs(to.x - from.x) / 2);
      const pathData = `M ${from.x} ${from.y} C ${from.x + deltaX} ${from.y}, ${
        to.x - deltaX
      } ${to.y}, ${to.x} ${to.y}`;

      return (
        <g key={id}>
          <path
            d={pathData}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={isDashed ? '4 2' : undefined}
          />
          <circle cx={from.x} cy={from.y} r="3.5" fill={color} />
          <circle cx={to.x} cy={to.y} r="3.5" fill={color} />
        </g>
      );
    };

    return (
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 9,
        }}
      >
        {elementLines.map((l) => renderCurve(l, false, 1.4))}
        {oprawaLines.map((l) => renderCurve(l, true, 2.2))}
        {singleLines.map((l) =>
          renderCurve({ ...l, id: `single_${l.id}`, color: '#38bdf8' }, false, 2.5)
        )}
      </svg>
    );
  }
);

// --- KOLORY KLOCKÓW ---

const computeStageColor = (stage, colorFrom) => {
  if (stage.typ_grupy == 1) {
    return `linear-gradient(to bottom, #cfcfcf67 5%, #eeeeee52 50%, #cfcfcf67 90%)`;
  }

  if (colorFrom === 1) {
    const str = String(stage.zamowienie_id || stage.klient_id);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 48%)`;
  }

  if (stage.status == 1) return `linear-gradient(to bottom, #7d7c7c7c 5%, #c9c9c97c 50%, #7d7c7c7c 90%)`;
  if (stage.status == 2) return `linear-gradient(to bottom, #a9ce54 5%, #c1e078 50%, #a9ce54 90%)`;
  if (stage.status == 3) return `linear-gradient(to bottom, #e3c956 5%, #f8df70 50%, #e3c956 90%)`;
  if (stage.status == 4 && stage.etap == 16) return `linear-gradient(to bottom, #e116d7 5%, #e116d7 50%, #e116d7 100%)`;
  if (stage.status == 4) return `linear-gradient(to bottom, #6c7c7c 5%, #9ca8a8 50%, #6c7c7c 100%)`;

  return `linear-gradient(to bottom, #3b82f6 5%, #60a5fa 50%, #2563eb 90%)`;
};

// --- POJEDYNCZY KLOCEK ---

const GantMultiStageBar = memo(
  ({
    stage,
    colorFrom,
    isSelected,
    isUnfit,
    unfitDelayText,
    isBeingDragged,
    onStageMouseDown,
    onStageClick,
  }) => {
    const isManual = stage._isManualTrack;
    const canDrag = !isManual && stage.typ_grupy != 1;
    const hasChangedProcessor =
      !isManual &&
      stage.procesor_id_symulacji &&
      String(stage.procesor_id_symulacji) !== String(stage.procesor_id);

    const barColor = !isManual && isUnfit
      ? `linear-gradient(to bottom, #ef4444 5%, #dc2626 50%, #991b1b 95%)`
      : computeStageColor(stage, colorFrom);

    const leftCss = `calc(${stage._startMinutes} * var(--scale) * 1px)`;
    const widthCss = `calc(${stage._durationMinutes} * var(--scale) * 1px)`;

    // Wyśrodkowanie góra/dół w obrębie każdego toru (Tor 1 oraz Tory 2+)
    const topPx = ROW_PADDING_Y + stage._laneIndex * LANE_HEIGHT + BAR_OFFSET_Y;

    return (
      <div
        className={`${styles.stageBar} ${
          !isManual && isUnfit
            ? styles.unfitBar
            : stage._isOverlapping
            ? styles.overlappingBar
            : ''
        } ${isSelected ? styles.selectedBar : ''}`}
        onMouseDown={
          canDrag
            ? (e) => {
                if (e.button !== 0) return;
                e.stopPropagation();
                onStageMouseDown(e, stage);
              }
            : undefined
        }
        onClick={(e) => {
          e.stopPropagation();
          onStageClick(stage);
        }}
        style={{
          left: leftCss,
          width: widthCss,
          top: `${topPx}px`,
          height: `${BAR_HEIGHT}px`,
          margin: 0,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          background: barColor,
          opacity: isManual ? 0.92 : 1,
          cursor: canDrag ? (isBeingDragged ? 'grabbing' : 'grab') : 'pointer',
          userSelect: 'none',
          outline: isBeingDragged ? '2px solid #10b981' : undefined,
          zIndex: isBeingDragged ? 45 : undefined,
          borderLeft:
            isManual && stage.typ_grupy != 1
              ? '3px solid #f59e0b'
              : hasChangedProcessor
              ? '3px solid #38bdf8'
              : undefined,
        }}
        title={stage._tooltipTitle}
      >
        <span
          className={stage.typ_grupy == 2 ? styles.nameText : styles.nameTextPrzerwa}
          style={!isManual && isUnfit ? { color: '#ffffff', fontWeight: 700 } : undefined}
        >
          {` ${stage.nr || ''}_${String(stage.rok || '').substring(2, 4)} ${
            stage.tytul || stage.uwagi || ''
          }`}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
          {!isManual && isUnfit && unfitDelayText && (
            <span className={styles.unfitDelayBadge} title="Proponowane opóźnienie daty spedycji">
              SPED +{unfitDelayText}
            </span>
          )}

          {!isManual && (stage.marginLeft !== 0 || stage.marginRight !== 0) && (
            <span className={styles.marginBadge}>
              {stage.marginLeft !== 0 ? `L:${stage.marginLeft}m ` : ''}
              {stage.marginRight !== 0 ? `R:${stage.marginRight}m` : ''}
            </span>
          )}
        </div>
      </div>
    );
  }
);

// --- GŁÓWNY KOMPONENT GANTMULTI ---

const GantMulti = ({ stages = [], onSaveSimulation }) => {
  const chartRef = useRef(null);
  const scrollWrapperRef = useRef(null);
  const stageNamesListRef = useRef(null);
  const scrollingSource = useRef(null);
  const animFrameId = useRef(null);
  const hasScrolledRef = useRef(false);
  const pendingScrollLeftRef = useRef(null);

  const dragSessionRef = useRef(null);
  const suppressNextClickRef = useRef(false);

  const navigate = useNavigate();
  const contextApp = useContext(AppContext);
  const { setGrupaWykonan } = useContext(TechnologyContext);
  const procesory = contextApp.procesory || [];

  const [scaleFactor, setScaleFactor] = useState(0.15);
  const [colorFrom, setColorFrom] = useState(4);
  const [selectedGrup, setSelectedGrup] = useState(null);
  const [selectedZamowienieId, setSelectedZamowienieId] = useState(null);

  const [showElementConections, setShowElementConections] = useState(true);
  const [showOprawaConnections, setShowOprawaConnections] = useState(true);
  const [showSingleConection, setShowSingleConection] = useState(true);

  const [customMargins, setCustomMargins] = useState({});
  const [customShipmentDelays, setCustomShipmentDelays] = useState({});
  const [customProdStartLimits, setCustomProdStartLimits] = useState({});
  const [customProcessors, setCustomProcessors] = useState({});

  // Rejestr UID klocków dotkniętych przez użytkownika w bieżącej sesji
  // Zerowany przez "Reset układu" oraz po udanym "Zapisz"
  const [dirtyStageUids, setDirtyStageUids] = useState(() => new Set());
  // Migawka zapisanych wartości (aktualizowana po kliknięciu "Zapisz", jeśli props stages nie odświeżył się z serwera)
  const [savedBaselineMap, setSavedBaselineMap] = useState({});

  const [unfitStageUids, setUnfitStageUids] = useState(new Set());
  const [unfitOrdersMap, setUnfitOrdersMap] = useState({});
  const [delaySuggestions, setDelaySuggestions] = useState([]);
  const [showDelayPanel, setShowDelayPanel] = useState(false);

  const [activeDragUi, setActiveDragUi] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Pomocnik oznaczający wybrane klocki jako zmodyfikowane (do zapisu)
  const markUidsDirty = useCallback((uids = []) => {
    if (!uids || uids.length === 0) return;
    setDirtyStageUids((prev) => {
      const next = new Set(prev);
      uids.forEach((u) => {
        if (u) next.add(u);
      });
      return next;
    });
  }, []);

  // 1. Przeliczenie wszystkich niezakończonych klocków (status != 4)
  const calculatedStages = useMemo(() => {
    return calculateBackwardStages(
      stages,
      customMargins,
      customShipmentDelays,
      customProdStartLimits,
      customProcessors,
      savedBaselineMap
    );
  }, [
    stages,
    customMargins,
    customShipmentDelays,
    customProdStartLimits,
    customProcessors,
    savedBaselineMap,
  ]);

  // Paczka zmodyfikowanych klocków gotowa do wysyłki na API.
  // Gdy klikniesz "Reset układu", dirtyStageUids zostaje wyczyszczony -> lista natychmiast ma 0 elementów.
  const modifiedSimulationStages = useMemo(() => {
    if (dirtyStageUids.size === 0) return [];

    return calculatedStages
      .filter((s) => {
        if (!s || s.typ_grupy == 1 || !dirtyStageUids.has(s._uid)) return false;

        const marginLeftChanged =
          Number(s.marginLeft || 0) !== Number(s._initMarginLeft || 0);
        const marginRightChanged =
          Number(s.marginRight || 0) !== Number(s._initMarginRight || 0);
        const processorChanged =
          String(s.procesor_id_symulacji ?? s.procesor_id) !==
          String(s._initSimProcesorId ?? s.procesor_id);
        const spedycjaChanged =
          String(s.spedycja_symulacji || '') !==
          String(s._initSimSpedycja || '');
        const rozpoczecieChanged =
          String(s.data_rozpoczecia_symulacji || '') !==
          String(s._initSimRozpoczecie || '');

        return (
          marginLeftChanged ||
          marginRightChanged ||
          processorChanged ||
          spedycjaChanged ||
          rozpoczecieChanged
        );
      })
      .map((s) => ({
        id: s.id,
        global_id: s.global_id,
        uid: s._uid,
        zamowienie_id: s.zamowienie_id,
        proces_nazwa_id: getProcesNazwaId(s),
        // Wymagane 5 pól do zapisu w bazie:
        data_rozpoczecia_symulacji: s.data_rozpoczecia_symulacji || null,
        spedycja_symulacji: s.spedycja_symulacji || null,
        data_spedycji_symulacji: s.data_spedycji_symulacji || null,
        procesor_id_symulacji: Number(s.procesor_id_symulacji ?? s.procesor_id),
        marginleft: Number(s.marginLeft || 0),
        marginright: Number(s.marginRight || 0),
        marginLeft: Number(s.marginLeft || 0),
        marginRight: Number(s.marginRight || 0),
        // Wyliczone daty położenia klocka w symulacji:
        poczatek_symulacji: s.poczatek_symulacji,
        koniec_symulacji: s.koniec_symulacji,
      }));
  }, [calculatedStages, dirtyStageUids]);

  const handleSaveSimulation = async () => {
    if (modifiedSimulationStages.length === 0 || isSaving) return;

    try {
      setIsSaving(true);
      console.log('Paczka zmodyfikowanych klocków symulacji:', modifiedSimulationStages);

      if (typeof onSaveSimulation === 'function') {
        await onSaveSimulation(modifiedSimulationStages);
      } else {
  // Przykład bezpośredniego strzału z axios (dopasuj URL do swojego routera):
        const token = sessionStorage.getItem('token');
        await axios.put(`${IP}/API_0025_zapis_symulacji/${token}`, {
          stages: modifiedSimulationStages,
        });
      }

      // Po udanym zapisie aktualizujemy punkt odniesienia i zerujemy listę do zapisu
      setSavedBaselineMap((prev) => {
        const next = { ...prev };
        modifiedSimulationStages.forEach((item) => {
          next[item.uid] = {
            marginLeft: item.marginLeft,
            marginRight: item.marginRight,
            procesor_id_symulacji: item.procesor_id_symulacji,
            spedycja_symulacji: item.spedycja_symulacji,
            data_rozpoczecia_symulacji: item.data_rozpoczecia_symulacji,
          };
        });
        return next;
      });
      setDirtyStageUids(new Set());
    } catch (err) {
      console.error('Błąd podczas zapisywania symulacji:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // 2. Grupowanie według procesora:
  // - Tor 1 (manualGroups): wg oryginalnego procesor_id
  // - Tory 2+ (simGroups): wg procesor_id_symulacji (z fallbackiem do procesor_id)
  const { groupedByProcessor, laneCountsByProcessor, simLaneCountsByProcessor } = useMemo(() => {
    const manualGroups = {};
    const simGroups = {};
    const allProcIdsSet = new Set();

    calculatedStages.forEach((stage) => {
      const origProcId = stage.procesor_id;
      const simProcId = stage.procesor_id_symulacji ?? stage.procesor_id;

      if (origProcId && !IGNORED_PROCESSOR_IDS.has(Number(origProcId))) {
        if (!manualGroups[origProcId]) manualGroups[origProcId] = [];
        manualGroups[origProcId].push(stage);
        allProcIdsSet.add(String(origProcId));
      }

      if (
        stage.typ_grupy != 1 &&
        simProcId &&
        !IGNORED_PROCESSOR_IDS.has(Number(simProcId))
      ) {
        if (!simGroups[simProcId]) simGroups[simProcId] = [];
        simGroups[simProcId].push(stage);
        allProcIdsSet.add(String(simProcId));
      }
    });

    const packedGroups = {};
    const laneCounts = {};
    const simLaneCounts = {};

    Array.from(allProcIdsSet).forEach((procId) => {
      const { packedStages, laneCount, simLaneCount } =
        buildProcessorLanesWithManualTrack(
          manualGroups[procId] || [],
          simGroups[procId] || []
        );
      packedGroups[procId] = packedStages;
      laneCounts[procId] = laneCount;
      simLaneCounts[procId] = simLaneCount;
    });

    return {
      groupedByProcessor: packedGroups,
      laneCountsByProcessor: laneCounts,
      simLaneCountsByProcessor: simLaneCounts,
    };
  }, [calculatedStages]);

  const processorIds = useMemo(
    () => Object.keys(groupedByProcessor),
    [groupedByProcessor]
  );

  const allPackedStages = useMemo(
    () => Object.values(groupedByProcessor).flat(),
    [groupedByProcessor]
  );

  // Rozsuwanie nachodzących klocków symulacji na JEDNYM wybranym procesorze
  const handleResolveSingleProcessor = useCallback(
    (processorId, baseStages = calculatedStages, baseMargins = customMargins) => {
      const result = resolveProcessorOverlaps({
        calculatedStages: mapStagesForResolver(baseStages),
        targetProcessorId: processorId,
        currentCustomMargins: baseMargins,
        customProdStartLimits,
        procesory,
        recalculateFn: (nextMargins) =>
          mapStagesForResolver(
            calculateBackwardStages(
              stages,
              nextMargins,
              customShipmentDelays,
              customProdStartLimits,
              customProcessors,
              savedBaselineMap
            )
          ),
      });

      // Oznaczamy klocki, którym algorytm zmienił marginesy, jako kwalifikujące się do zapisu
      const changedUids = Object.keys(result.updatedMargins || {}).filter(
        (uid) =>
          result.updatedMargins[uid]?.marginRight !== baseMargins[uid]?.marginRight ||
          result.updatedMargins[uid]?.marginLeft !== baseMargins[uid]?.marginLeft
      );
      markUidsDirty(changedUids);

      setCustomMargins(result.updatedMargins);

      setUnfitStageUids((prev) => {
        const next = new Set(prev);
        baseStages
          .filter(
            (s) =>
              String(s.procesor_id_symulacji ?? s.procesor_id) ===
              String(processorId)
          )
          .forEach((s) => next.delete(s._uid));
        result.unfitStageUids.forEach((uid) => next.add(uid));
        return next;
      });

      setUnfitOrdersMap((prev) => ({ ...prev, ...result.unfitOrdersMap }));

      setDelaySuggestions((prev) => {
        const filtered = prev.filter(
          (item) => String(item.procesorId) !== String(processorId)
        );
        const combined = [...filtered, ...result.suggestions];
        if (combined.length > 0) setShowDelayPanel(true);
        return combined;
      });
    },
    [
      calculatedStages,
      customMargins,
      stages,
      customShipmentDelays,
      customProdStartLimits,
      customProcessors,
      savedBaselineMap,
      procesory,
      markUidsDirty,
    ]
  );

  // Rozsuwanie nachodzących klocków symulacji na WSZYSTKICH procesorach naraz
  const handleResolveAllProcessors = useCallback(() => {
    let workingMargins = { ...customMargins };
    let workingStages = calculatedStages;
    const allUnfitUids = new Set();
    let allUnfitOrders = {};
    let allSuggestions = [];

    const orderedProcIds = [...processorIds].sort((a, b) => {
      const isOprawaA = Number(a) === 8 || Number(a) === 10 ? 1 : 0;
      const isOprawaB = Number(b) === 8 || Number(b) === 10 ? 1 : 0;
      return isOprawaB - isOprawaA;
    });

    orderedProcIds.forEach((procId) => {
      const res = resolveProcessorOverlaps({
        calculatedStages: mapStagesForResolver(workingStages),
        targetProcessorId: procId,
        currentCustomMargins: workingMargins,
        customProdStartLimits,
        procesory,
        recalculateFn: (nextMargins) =>
          mapStagesForResolver(
            calculateBackwardStages(
              stages,
              nextMargins,
              customShipmentDelays,
              customProdStartLimits,
              customProcessors,
              savedBaselineMap
            )
          ),
      });

      workingMargins = res.updatedMargins;
      workingStages = calculateBackwardStages(
        stages,
        workingMargins,
        customShipmentDelays,
        customProdStartLimits,
        customProcessors,
        savedBaselineMap
      );

      res.unfitStageUids.forEach((uid) => allUnfitUids.add(uid));
      allUnfitOrders = { ...allUnfitOrders, ...res.unfitOrdersMap };
      allSuggestions = [...allSuggestions, ...res.suggestions];
    });

    const changedUids = Object.keys(workingMargins || {}).filter(
      (uid) =>
        workingMargins[uid]?.marginRight !== customMargins[uid]?.marginRight ||
        workingMargins[uid]?.marginLeft !== customMargins[uid]?.marginLeft
    );
    markUidsDirty(changedUids);

    setCustomMargins(workingMargins);
    setUnfitStageUids(allUnfitUids);
    setUnfitOrdersMap(allUnfitOrders);
    setDelaySuggestions(allSuggestions);
    if (allSuggestions.length > 0) setShowDelayPanel(true);
  }, [
    processorIds,
    customMargins,
    calculatedStages,
    stages,
    customShipmentDelays,
    customProdStartLimits,
    customProcessors,
    savedBaselineMap,
    procesory,
    markUidsDirty,
  ]);

  // Zastosowanie opóźnienia spedycji -> zapisuje nową datę w spedycja_symulacji
  const handleApplyShipmentDelay = useCallback(
    (zamowienieId, delayMs) => {
      const zamIdStr = String(zamowienieId);
      const nextDelays = {
        ...customShipmentDelays,
        [zamIdStr]: (customShipmentDelays[zamIdStr] || 0) + delayMs,
      };
      setCustomShipmentDelays(nextDelays);

      const orderStageUids = calculatedStages
        .filter((s) => String(s.zamowienie_id) === zamIdStr)
        .map((s) => s._uid);
      markUidsDirty(orderStageUids);

      setDelaySuggestions((prev) =>
        prev.filter((item) => String(item.zamowienieId) !== zamIdStr)
      );
      setUnfitOrdersMap((prev) => {
        const copy = { ...prev };
        delete copy[zamIdStr];
        return copy;
      });
      setUnfitStageUids((prev) => {
        const next = new Set(prev);
        orderStageUids.forEach((uid) => next.delete(uid));
        return next;
      });
    },
    [customShipmentDelays, calculatedStages, markUidsDirty]
  );

  // Zastosowanie przesunięcia startu produkcji -> zapisuje nową datę w data_rozpoczecia_symulacji
  const handleApplyProdStartShift = useCallback(
    (zamowienieId, newProdStartLimitMs, processorId) => {
      const zamIdStr = String(zamowienieId);
      const nextLimits = {
        ...customProdStartLimits,
        [zamIdStr]: newProdStartLimitMs - 60000,
      };
      setCustomProdStartLimits(nextLimits);

      const updatedStagesWithLimit = calculateBackwardStages(
        stages,
        customMargins,
        customShipmentDelays,
        nextLimits,
        customProcessors,
        savedBaselineMap
      );

      const result = resolveProcessorOverlaps({
        calculatedStages: mapStagesForResolver(updatedStagesWithLimit),
        targetProcessorId: processorId,
        currentCustomMargins: customMargins,
        customProdStartLimits: nextLimits,
        procesory,
        recalculateFn: (nextMargins) =>
          mapStagesForResolver(
            calculateBackwardStages(
              stages,
              nextMargins,
              customShipmentDelays,
              nextLimits,
              customProcessors,
              savedBaselineMap
            )
          ),
      });

      const orderUids = updatedStagesWithLimit
        .filter((s) => String(s.zamowienie_id) === zamIdStr)
        .map((s) => s._uid);
      const changedMarginUids = Object.keys(result.updatedMargins || {});
      markUidsDirty([...orderUids, ...changedMarginUids]);

      setCustomMargins(result.updatedMargins);
      setUnfitStageUids((prev) => {
        const next = new Set(prev);
        updatedStagesWithLimit
          .filter(
            (s) =>
              String(s.procesor_id_symulacji ?? s.procesor_id) ===
              String(processorId)
          )
          .forEach((s) => next.delete(s._uid));
        result.unfitStageUids.forEach((uid) => next.add(uid));
        return next;
      });
      setDelaySuggestions((prev) => {
        const filtered = prev.filter(
          (item) => String(item.procesorId) !== String(processorId)
        );
        return [...filtered, ...result.suggestions];
      });
    },
    [
      customProdStartLimits,
      customMargins,
      stages,
      customShipmentDelays,
      customProcessors,
      savedBaselineMap,
      procesory,
      markUidsDirty,
    ]
  );

  // Zastosowanie propozycji przeniesienia klocka z panelu GantMultiDelayPanel
  const handleApplyProcessorChangeFromPanel = useCallback(
    (stageUid, zamowienieId, newProcessorId, additionalMarginMin = 0) => {
      const zamIdStr = String(zamowienieId);

      setCustomProcessors((prev) => ({
        ...prev,
        [stageUid]: Number(newProcessorId),
      }));

      if (additionalMarginMin > 0) {
        setCustomMargins((prev) => {
          const targetStage = calculatedStages.find((s) => s._uid === stageUid);
          const current = prev[stageUid] || {
            marginLeft: targetStage?.marginLeft || 0,
            marginRight: targetStage?.marginRight || 0,
          };
          return {
            ...prev,
            [stageUid]: {
              ...current,
              marginRight: (current.marginRight || 0) + additionalMarginMin,
            },
          };
        });
      }

      markUidsDirty([stageUid]);

      setUnfitStageUids((prev) => {
        const next = new Set(prev);
        next.delete(stageUid);
        return next;
      });

      setDelaySuggestions((prev) => {
        const nextList = prev.filter((item) => item.uid !== stageUid);
        const stillUnfitForOrder = nextList.find(
          (item) => String(item.zamowienieId) === zamIdStr
        );
        setUnfitOrdersMap((prevMap) => {
          const copy = { ...prevMap };
          if (stillUnfitForOrder) {
            copy[zamIdStr] = stillUnfitForOrder;
          } else {
            delete copy[zamIdStr];
          }
          return copy;
        });
        return nextList;
      });
    },
    [calculatedStages, markUidsDirty]
  );

  // Ręczna zmiana procesora z dolnego menu (tylko druk [1,2,3] lub falcowanie [14..21])
  const handleChangeSimulatedProcessor = (newProcId) => {
    if (!selectedGrup) return;
    const allowedIds = getAllowedProcessorIdsForStage(selectedGrup);
    const currentProcId = Number(
      selectedGrup.procesor_id_symulacji ?? selectedGrup.procesor_id
    );
    const targetIdNum = Number(newProcId);

    if (targetIdNum !== currentProcId && !allowedIds.includes(targetIdNum)) {
      return;
    }

    const uid = getStageUid(selectedGrup);
    setCustomProcessors((prev) => ({
      ...prev,
      [uid]: targetIdNum,
    }));
    markUidsDirty([uid]);
  };

  // RESET UKŁADU -> cofa wszystkie przesunięcia i ZERUJE LISTĘ DO ZAPISU
  const handleClearAllResolutions = useCallback(() => {
    setCustomMargins({});
    setCustomShipmentDelays({});
    setCustomProdStartLimits({});
    setCustomProcessors({});
    setDirtyStageUids(new Set()); // <-- Zerowanie listy do zapisu
    setUnfitStageUids(new Set());
    setUnfitOrdersMap({});
    setDelaySuggestions([]);
    setShowDelayPanel(false);
  }, []);

  // 3. Wyznaczenie zakresu osi czasu [minDate, maxDate]
  const { minDate, maxDate } = useMemo(() => {
    if (allPackedStages.length === 0) {
      const now = new Date();
      return {
        minDate: new Date(now.getTime() - 24 * 3600 * 1000),
        maxDate: new Date(now.getTime() + 7 * 24 * 3600 * 1000),
      };
    }

    let minMs = Infinity;
    let maxMs = -Infinity;

    allPackedStages.forEach((s) => {
      if (!Number.isNaN(s._renderStartMs) && s._renderStartMs < minMs) minMs = s._renderStartMs;
      if (!Number.isNaN(s._renderEndMs) && s._renderEndMs > maxMs) maxMs = s._renderEndMs;

      const activeShip =
        s.spedycja_symulacji || s.data_spedycji_symulacji || s.data_spedycji;
      if (activeShip) {
        const shipMs = getShipmentTimestampMs(activeShip);
        if (!Number.isNaN(shipMs) && shipMs > maxMs) maxMs = shipMs;
      }
    });

    const dMin = new Date(minMs);
    const dMax = new Date(maxMs);
    dMin.setHours(dMin.getHours() - 12, 0, 0, 0);
    dMax.setHours(dMax.getHours() + 48, 0, 0, 0);

    return { minDate: dMin, maxDate: dMax };
  }, [allPackedStages]);

  const totalChartWidth = useMemo(() => {
    if (!minDate || !maxDate) return 0;
    const totalMinutes = (maxDate.getTime() - minDate.getTime()) / 60000;
    return totalMinutes * scaleFactor;
  }, [minDate, maxDate, scaleFactor]);

  // 4. Synchroniczne wyliczanie dokładnej górnej krawędzi każdego wiersza
  const rowTopPositions = useMemo(() => {
    const tops = {};
    let currentTop = TIMELINE_HEADER_HEIGHT;

    processorIds.forEach((procId) => {
      const lanes = laneCountsByProcessor[procId] || 1;
      const rowHeight = lanes * LANE_HEIGHT + ROW_PADDING_Y * 2;
      tops[procId] = currentTop;
      currentTop += rowHeight;
    });

    return tops;
  }, [processorIds, laneCountsByProcessor]);

  // 5. Przygotowanie minut startu/trwania, indywidualnego środka Y (_centerY) oraz tooltipów
  const preparedGroupedStages = useMemo(() => {
    if (!minDate) return groupedByProcessor;
    const minTimeMs = minDate.getTime();
    const result = {};

    Object.keys(groupedByProcessor).forEach((procId) => {
      const rowTop = rowTopPositions[procId] ?? TIMELINE_HEADER_HEIGHT;

      result[procId] = groupedByProcessor[procId].map((stage) => {
        const startMinutes = Math.max(0, (stage._renderStartMs - minTimeMs) / 60000);
        const durationMinutes = Math.max(1, stage._renderDurationMs / 60000);

        const stageCenterY =
          rowTop +
          ROW_PADDING_Y +
          (stage._laneIndex || 0) * LANE_HEIGHT +
          LANE_HEIGHT / 2;

        const origProcName =
          procesory.find((x) => String(x.id) === String(stage.procesor_id))?.nazwa ||
          stage.procesor_id;
        const simProcName =
          procesory.find((x) => String(x.id) === String(stage.procesor_id_symulacji))?.nazwa ||
          stage.procesor_id_symulacji;

        const trackLabel = stage._isManualTrack
          ? stage.typ_grupy == 1
            ? 'Tor 1 (Przerwa - typ_grupy 1)'
            : 'Tor 1 (Dane wejściowe - ręczne)'
          : `Tor ${stage._laneIndex + 1} (Propozycja symulacji #${stage._laneIndex})`;

        const tooltipTitle = `${stage.nr || ''} ${stage.rok || ''} ${stage.firma_nazwa || ''} ${stage.tytul || stage.uwagi || ''}
[${trackLabel}]
Wejście (start - end): ${date_time(stage.start)}  ->  ${date_time(stage.end)} (${origProcName})
${
  stage.typ_grupy == 1
    ? ''
    : `Symulacja (poczatek - koniec): ${date_time(stage.poczatek_symulacji)}  ->  ${date_time(stage.koniec_symulacji)} (${simProcName})\n`
}Spedycja (wejście / symulacja): ${date_bez_time(stage.data_spedycji) || '--'}  /  ${date_time(stage.spedycja_symulacji) || '--'}
Start prod. (wejście / symulacja): ${stage.data_rozpoczecia_produkcji || 'brak (-5d)'}  /  ${stage.data_rozpoczecia_symulacji || 'brak (-5d)'}
marginLeft: ${stage.marginLeft} min | marginRight: ${stage.marginRight} min
Element: ${getNameOfElementTyp(stage.typ_elementu, contextApp.typ_elementu) || '--'}
Indeks procesu: ${stage.indeks_procesu ?? '--'} | Arkusze: ${stage.arkusze_id || 'brak'}
global_id: ${stage.global_id}
`;

        return {
          ...stage,
          _startMinutes: startMinutes,
          _durationMinutes: durationMinutes,
          _centerY: stageCenterY,
          _tooltipTitle: tooltipTitle,
        };
      });
    });

    return result;
  }, [groupedByProcessor, minDate, rowTopPositions, contextApp.typ_elementu, procesory]);

  const allPreparedStages = useMemo(
    () => Object.values(preparedGroupedStages).flat(),
    [preparedGroupedStages]
  );

  const layoutGeometryRef = useRef({
    scaleFactor,
    processorIds,
    rowTopPositions,
    laneCountsByProcessor,
  });
  layoutGeometryRef.current = {
    scaleFactor,
    processorIds,
    rowTopPositions,
    laneCountsByProcessor,
  };

  // --- OBSŁUGA PRZECIĄGANIA KLOCKÓW (DRAG & DROP) ORAZ KLIKANIA ---

  const handleStageMouseDown = useCallback(
    (e, stage) => {
      if (stage._isManualTrack || stage.typ_grupy == 1) return;

      const currentSimProcId = Number(
        stage.procesor_id_symulacji ?? stage.procesor_id
      );
      const poolIds = getAllowedProcessorIdsForStage(stage);
      const allowedProcessorIds = Array.from(
        new Set([currentSimProcId, ...poolIds.map(Number)])
      );

      dragSessionRef.current = {
        stage,
        uid: stage._uid,
        startClientX: e.clientX,
        startClientY: e.clientY,
        origMarginLeft: stage.marginLeft || 0,
        origMarginRight: stage.marginRight || 0,
        origProcessorId: currentSimProcId,
        allowedProcessorIds,
        targetProcessorId: currentSimProcId,
        hoveredProcessorId: currentSimProcId,
        lastAppliedDeltaMin: 0,
        lastAppliedProcId: currentSimProcId,
        hasMoved: false,
      };

      const handleWindowMouseMove = (moveEvt) => {
        const session = dragSessionRef.current;
        if (!session) return;

        const dx = moveEvt.clientX - session.startClientX;
        const dy = moveEvt.clientY - session.startClientY;

        if (!session.hasMoved) {
          if (
            Math.abs(dx) < DRAG_THRESHOLD_PX &&
            Math.abs(dy) < DRAG_THRESHOLD_PX
          ) {
            return;
          }
          session.hasMoved = true;
          suppressNextClickRef.current = true;
          setGrupaWykonan?.([]);
          setSelectedGrup(session.stage);
          setSelectedZamowienieId(session.stage.zamowienie_id);
        }

        const {
          scaleFactor: curScale,
          processorIds: curProcIds,
          rowTopPositions: curRowTops,
          laneCountsByProcessor: curLaneCounts,
        } = layoutGeometryRef.current;

        const rawDeltaMin = dx / (curScale || 0.15);
        const snapStepMin = curScale >= 0.5 ? 1 : 5;
        const snappedDeltaMinutes =
          Math.round(rawDeltaMin / snapStepMin) * snapStepMin;

        let hoveredProcId = session.origProcessorId;
        if (chartRef.current) {
          const chartRect = chartRef.current.getBoundingClientRect();
          const relY = moveEvt.clientY - chartRect.top;

          for (let i = 0; i < curProcIds.length; i++) {
            const procId = curProcIds[i];
            const rTop = curRowTops[procId] ?? TIMELINE_HEADER_HEIGHT;
            const rHeight =
              (curLaneCounts[procId] || 1) * LANE_HEIGHT + ROW_PADDING_Y * 2;
            if (relY >= rTop && relY <= rTop + rHeight) {
              hoveredProcId = Number(procId);
              break;
            }
          }
        }

        const isAllowedProc = session.allowedProcessorIds.includes(
          Number(hoveredProcId)
        );
        const nextTargetProcId = isAllowedProc
          ? Number(hoveredProcId)
          : session.targetProcessorId;

        session.hoveredProcessorId = hoveredProcId;
        session.targetProcessorId = nextTargetProcId;

        if (snappedDeltaMinutes !== session.lastAppliedDeltaMin) {
          session.lastAppliedDeltaMin = snappedDeltaMinutes;
          const nextMarginRight = session.origMarginRight - snappedDeltaMinutes;

          setCustomMargins((prev) => ({
            ...prev,
            [session.uid]: {
              marginLeft: session.origMarginLeft,
              marginRight: nextMarginRight,
            },
          }));
          markUidsDirty([session.uid]);
        }

        if (nextTargetProcId !== session.lastAppliedProcId) {
          session.lastAppliedProcId = nextTargetProcId;
          setCustomProcessors((prev) => ({
            ...prev,
            [session.uid]: Number(nextTargetProcId),
          }));
          markUidsDirty([session.uid]);
        }

        setActiveDragUi({
          uid: session.uid,
          allowedProcessorIds: session.allowedProcessorIds,
          targetProcessorId: nextTargetProcId,
          hoveredProcessorId: hoveredProcId,
          isInvalidProcessorHover: !isAllowedProc,
        });
      };

      const handleWindowMouseUp = () => {
        const session = dragSessionRef.current;
        if (session && session.hasMoved) {
          if (
            session.lastAppliedDeltaMin !== 0 ||
            Number(session.targetProcessorId) !== Number(session.origProcessorId)
          ) {
            markUidsDirty([session.uid]);
            setUnfitStageUids((prev) => {
              if (!prev.has(session.uid)) return prev;
              const next = new Set(prev);
              next.delete(session.uid);
              return next;
            });
          }
        }

        dragSessionRef.current = null;
        setActiveDragUi(null);
        window.removeEventListener('mousemove', handleWindowMouseMove);
        window.removeEventListener('mouseup', handleWindowMouseUp);
      };

      window.addEventListener('mousemove', handleWindowMouseMove);
      window.addEventListener('mouseup', handleWindowMouseUp);
    },
    [setGrupaWykonan, markUidsDirty]
  );

  const ganttZoom = useGanttZoom({
    chartRef,
    scrollWrapperRef,
    minDate,
    maxDate,
    totalChartWidth,
    scaleFactor,
    setScaleFactor,
  });

  const syncScroll = (source, target) => {
    if (scrollingSource.current && scrollingSource.current !== source) return;
    scrollingSource.current = source;

    if (target.current && source.current) {
      target.current.scrollTop = source.current.scrollTop;
    }

    if (animFrameId.current) cancelAnimationFrame(animFrameId.current);
    animFrameId.current = requestAnimationFrame(() => {
      scrollingSource.current = null;
    });
  };

  const handleStageClick = useCallback(
    (stage) => {
      if (suppressNextClickRef.current) {
        suppressNextClickRef.current = false;
        return;
      }
      setGrupaWykonan?.([]);
      const isSame =
        selectedGrup && getStageUid(selectedGrup) === getStageUid(stage);
      setSelectedGrup(isSame ? null : stage);
      setSelectedZamowienieId(isSame ? null : stage.zamowienie_id);
    },
    [selectedGrup, setGrupaWykonan]
  );

  const handleAdjustMargin = (side, deltaMinutes) => {
    if (!selectedGrup) return;
    const uid = getStageUid(selectedGrup);

    setCustomMargins((prev) => {
      const current = prev[uid] || {
        marginLeft: selectedGrup.marginLeft || 0,
        marginRight: selectedGrup.marginRight || 0,
      };
      const updated = {
        ...current,
        [side]: (current[side] || 0) + deltaMinutes,
      };
      return { ...prev, [uid]: updated };
    });
    markUidsDirty([uid]);
  };

  const handleResetStageMargins = () => {
    if (!selectedGrup) return;
    const uid = getStageUid(selectedGrup);
    setCustomMargins((prev) => ({
      ...prev,
      [uid]: { marginLeft: 0, marginRight: 0 },
    }));
    markUidsDirty([uid]);
  };

  const currentSelectedStage = useMemo(() => {
    if (!selectedGrup) return null;
    const uid = getStageUid(selectedGrup);
    return (
      allPreparedStages.find((s) => s._uid === uid && !s._isManualTrack) ||
      allPreparedStages.find((s) => s._uid === uid) ||
      null
    );
  }, [selectedGrup, allPreparedStages]);

  const allowedProcessorsForSelected = useMemo(() => {
    if (!currentSelectedStage) return [];

    const allowedIds = getAllowedProcessorIdsForStage(currentSelectedStage);
    const currentProcId = Number(
      currentSelectedStage.procesor_id_symulacji ??
        currentSelectedStage.procesor_id
    );

    if (allowedIds.length === 0) {
      return procesory.filter((p) => Number(p.id) === currentProcId);
    }

    return procesory.filter(
      (p) => allowedIds.includes(Number(p.id)) || Number(p.id) === currentProcId
    );
  }, [currentSelectedStage, procesory]);

  const activeSelectedShipmentDate = useMemo(() => {
    if (!currentSelectedStage) return null;
    return (
      currentSelectedStage.spedycja_symulacji ||
      currentSelectedStage.data_spedycji_symulacji ||
      currentSelectedStage.data_spedycji ||
      null
    );
  }, [currentSelectedStage]);

  const calculateDatePositionPx = (dateVal, hour = null) => {
    if (!minDate || !maxDate || !totalChartWidth || !dateVal) return 0;
    const d = new Date(dateVal);
    if (hour !== null) d.setHours(hour, 0, 0, 0);
    const totalSpanMs = maxDate.getTime() - minDate.getTime();
    const offsetMs = d.getTime() - minDate.getTime();
    return Math.max(0, Math.min((offsetMs / totalSpanMs) * totalChartWidth, totalChartWidth));
  };

  const handleScrollToNow = useCallback(() => {
    if (!scrollWrapperRef.current || !minDate || !maxDate || !totalChartWidth) return;
    const nowPx = calculateDatePositionPx(new Date());
    scrollWrapperRef.current.scrollLeft =
      nowPx - scrollWrapperRef.current.clientWidth / 2;
  }, [minDate, maxDate, totalChartWidth]);

  useEffect(() => {
    if (totalChartWidth > 0 && !hasScrolledRef.current) {
      const timer = setTimeout(() => {
        handleScrollToNow();
        hasScrolledRef.current = true;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [totalChartWidth, handleScrollToNow]);

  useLayoutEffect(() => {
    if (pendingScrollLeftRef.current !== null && scrollWrapperRef.current) {
      scrollWrapperRef.current.scrollLeft = pendingScrollLeftRef.current;
      pendingScrollLeftRef.current = null;
    }
  }, [scaleFactor, totalChartWidth]);

  const handleFitOrderToScreen = () => {
    if (!selectedZamowienieId || !scrollWrapperRef.current || !minDate || !maxDate) return;
    const orderStages = allPreparedStages.filter(
      (s) => String(s.zamowienie_id) === String(selectedZamowienieId)
    );
    if (orderStages.length === 0) return;

    const earliestMs = Math.min(...orderStages.map((s) => s._renderStartMs));
    let latestMs = Math.max(...orderStages.map((s) => s._renderEndMs));

    if (activeSelectedShipmentDate) {
      const shipMs = getShipmentTimestampMs(activeSelectedShipmentDate);
      if (!Number.isNaN(shipMs) && shipMs > latestMs) latestMs = shipMs;
    }

    const durationMin = Math.max(60, (latestMs - earliestMs) / 60000);
    const containerWidth = Math.max(200, scrollWrapperRef.current.clientWidth - 80);
    const newScale = Math.min(Math.max(containerWidth / durationMin, 0.02), 3.0);

    const totalChartMin = (maxDate.getTime() - minDate.getTime()) / 60000;
    const estimatedWidth = totalChartMin * newScale;
    const centerRatio =
      (earliestMs + (latestMs - earliestMs) / 2 - minDate.getTime()) /
      (maxDate.getTime() - minDate.getTime());

    pendingScrollLeftRef.current = Math.max(
      0,
      centerRatio * estimatedWidth - scrollWrapperRef.current.clientWidth / 2
    );
    setScaleFactor(newScale);
  };

  // Osie czasu, weekendy i siatka godzinowa
  const { weekendBlocks, hourLines, timelineMarkers } = useMemo(() => {
    if (!minDate || !maxDate || totalChartWidth === 0) {
      return { weekendBlocks: null, hourLines: null, timelineMarkers: null };
    }

    const weekends = [];
    const lines = [];
    const markers = [];
    const daysOfWeek = ['N', 'Pn', 'Wt', 'Śr', 'Czw', 'Pt', 'So'];
    const totalSpanMs = maxDate.getTime() - minDate.getTime();

    const cur = new Date(minDate);
    cur.setHours(0, 0, 0, 0);
    let dayIdx = 0;

    while (cur <= maxDate) {
      const dayOfWeek = cur.getDay();
      const dayOffsetMs = cur.getTime() - minDate.getTime();
      const dayLeftPx = (dayOffsetMs / totalSpanMs) * totalChartWidth;

      markers.push(
        <div
          key={`marker-day-${dayIdx}`}
          className={styles.timelineMarker}
          style={{ left: `${dayLeftPx}px` }}
        >
          {daysOfWeek[dayOfWeek]} {String(cur.getDate()).padStart(2, '0')}/
          {String(cur.getMonth() + 1).padStart(2, '0')}
        </div>
      );

      if (dayOfWeek === 6 || dayOfWeek === 0) {
        const dayEnd = new Date(cur);
        dayEnd.setHours(23, 59, 59, 999);
        const wStart = cur < minDate ? minDate : cur;
        const wEnd = dayEnd > maxDate ? maxDate : dayEnd;
        const wLeft = ((wStart.getTime() - minDate.getTime()) / totalSpanMs) * totalChartWidth;
        const wWidth = ((wEnd.getTime() - wStart.getTime()) / totalSpanMs) * totalChartWidth;

        weekends.push(
          <div
            key={`weekend-${dayIdx}`}
            className={styles.weekendOverlay}
            style={{ left: `${wLeft}px`, width: `${wWidth}px` }}
          />
        );
      }

      const hoursToAdd =
        scaleFactor < 0.3
          ? [6, 18]
          : [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22];

      hoursToAdd.forEach((h) => {
        const hDate = new Date(cur);
        hDate.setHours(h, 0, 0, 0);
        if (hDate >= minDate && hDate <= maxDate) {
          const hLeft = ((hDate.getTime() - minDate.getTime()) / totalSpanMs) * totalChartWidth;
          lines.push(
            <div
              key={`hline-${dayIdx}-${h}`}
              className={styles.hourLineGrid}
              style={{ left: `${hLeft}px` }}
            />
          );
          markers.push(
            <div
              key={`hmarker-${dayIdx}-${h}`}
              className={styles.timelineSubMarker}
              style={{ left: `${hLeft}px` }}
            >
              {h}:00
            </div>
          );
        }
      });

      cur.setDate(cur.getDate() + 1);
      dayIdx++;
    }

    return { weekendBlocks: weekends, hourLines: lines, timelineMarkers: markers };
  }, [minDate, maxDate, totalChartWidth, scaleFactor]);

  const overlappingCount = useMemo(
    () => allPreparedStages.filter((s) => !s._isManualTrack && s._isOverlapping).length,
    [allPreparedStages]
  );

  const effectiveShipmentPx = useMemo(() => {
    if (!activeSelectedShipmentDate || !minDate || !maxDate || !totalChartWidth) return 0;
    const shipMs = getShipmentTimestampMs(activeSelectedShipmentDate);
    if (Number.isNaN(shipMs)) return 0;
    return calculateDatePositionPx(new Date(shipMs));
  }, [activeSelectedShipmentDate, minDate, maxDate, totalChartWidth]);

  const hasAnySessionChanges =
    dirtyStageUids.size > 0 ||
    Object.keys(customMargins).length > 0 ||
    Object.keys(customShipmentDelays).length > 0 ||
    Object.keys(customProdStartLimits).length > 0 ||
    Object.keys(customProcessors).length > 0 ||
    delaySuggestions.length > 0;

  return (
    <div className={styles.chartContainer}>
      {/* TOP HEADER */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.chartTitle}>
            <Layers size={18} /> Symulator (Tor 1: Wejście | Tory 2+: Symulacja -24h)
          </span>
          <span className={styles.overlapCounter}>
            Nachodzące (symulacja): <strong>{overlappingCount}</strong>
          </span>

          <button
            className={styles.toolBtn}
            onClick={handleResolveAllProcessors}
            style={{ backgroundColor: '#6d28d9', borderColor: '#8b5cf6', fontWeight: 600 }}
            title="Rozsuń nachodzące klocki symulacji na wszystkich procesorach"
          >
            <Sparkles size={15} /> Rozsuń wszystkie
          </button>

          {/* Przycisk ZAPISZ pojawia się, gdy lista do zapisu (modifiedSimulationStages) nie jest pusta */}
          {modifiedSimulationStages.length > 0 && (
            <button
              className={styles.toolBtn}
              onClick={handleSaveSimulation}
              disabled={isSaving}
              style={{
                backgroundColor: '#059669',
                borderColor: '#10b981',
                fontWeight: 700,
              }}
              title="Wyślij na API paczkę zmodyfikowanych klocków (data_rozpoczecia_symulacji, spedycja_symulacji, procesor_id_symulacji, marginleft, marginright)"
            >
              <Save size={14} />{' '}
              {isSaving
                ? 'Zapisywanie...'
                : `Zapisz (${modifiedSimulationStages.length})`}
            </button>
          )}

          {/* Reset układu cofa przesunięcia i zeruje listę do zapisu */}
          {hasAnySessionChanges && (
            <button
              className={styles.toolBtn}
              onClick={handleClearAllResolutions}
              style={{ backgroundColor: '#991b1b', borderColor: '#dc2626' }}
              title="Zresetuj niezapisane zmiany symulacji i wyzeruj listę do zapisu"
            >
              <RotateCcw size={14} /> Reset układu
            </button>
          )}
        </div>

        <div className={styles.controls}>
          <GanttZoomButtons zoom={ganttZoom} buttonClassName={styles.toolBtn} />

          <button
            className={styles.toolBtn}
            onClick={() => setColorFrom((prev) => (prev === 1 ? 4 : 1))}
            style={{ backgroundColor: colorFrom === 1 ? '#2563eb' : '#374151' }}
            title="Przełącz kolorowanie: wg Zamówienia / wg Statusu"
          >
            Kolor: {colorFrom === 1 ? 'Zlecenie' : 'Status'}
          </button>

          <button
            className={styles.toolBtn}
            onClick={() => setShowSingleConection((prev) => !prev)}
            style={{ backgroundColor: showSingleConection ? '#19675e' : '#374151' }}
            title="Połączenia zaznaczonego klocka"
          >
            <Spline size={18} />
          </button>

          <button
            className={styles.toolBtn}
            onClick={() => setShowElementConections((prev) => !prev)}
            style={{ backgroundColor: showElementConections ? '#19675e' : '#374151' }}
            title="Połączenia elementów"
          >
            <Waypoints size={18} />
          </button>

          <button
            className={styles.toolBtn}
            onClick={() => setShowOprawaConnections((prev) => !prev)}
            style={{ backgroundColor: showOprawaConnections ? '#19675e' : '#374151' }}
            title="Połączenia z oprawą"
          >
            <Waypoints size={18} style={{ rotate: '90deg' }} />
          </button>

          <button
            className={styles.closeBtn}
            onClick={() => navigate('/Panel')}
            title="Zamknij"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* CENTER WORKSPACE */}
      <div className={styles.chartCenter}>
        <div className={styles.ganttWrapper}>
          {/* Lewa kolumna */}
          <div
            className={styles.stageNamesList}
            ref={stageNamesListRef}
            onWheel={(e) => {
              if (scrollWrapperRef.current) {
                scrollWrapperRef.current.scrollTop += e.deltaY;
              }
            }}
          >
            <div className={styles.timelineHeaderSpacer} />

            <div className={styles.rowsContentContainer}>
              {processorIds.map((processorId) => {
                const lanes = laneCountsByProcessor[processorId] || 1;
                const simLanes = simLaneCountsByProcessor[processorId] || 0;
                const rowHeight = lanes * LANE_HEIGHT + ROW_PADDING_Y * 2;
                const procName =
                  procesory.find((x) => String(x.id) === String(processorId))?.nazwa ||
                  `Procesor ${processorId}`;

                const isAllowedDropRow =
                  activeDragUi &&
                  activeDragUi.allowedProcessorIds.includes(Number(processorId));
                const isTargetDropRow =
                  activeDragUi &&
                  Number(activeDragUi.targetProcessorId) === Number(processorId);

                return (
                  <div
                    key={`left-proc-${processorId}`}
                    className={styles.processorRowLeft}
                    style={{
                      height: `${rowHeight}px`,
                      backgroundColor: isTargetDropRow
                        ? 'rgba(16, 185, 129, 0.18)'
                        : isAllowedDropRow
                        ? 'rgba(56, 189, 248, 0.08)'
                        : undefined,
                    }}
                  >
                    <div className={styles.processorNameFixed}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span>{procName}</span>
                        <span style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 400 }}>
                          Tor 1: Wejście | Tory 2+: Symulacja
                        </span>
                      </div>
                      {simLanes > 1 && (
                        <button
                          className={styles.resolveLaneBtn}
                          onClick={() => handleResolveSingleProcessor(processorId)}
                          title="Rozsuń nachodzące klocki symulacji na tym procesorze"
                        >
                          <Sparkles size={11} /> Rozsuń ({simLanes}x)
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Prawa kolumna */}
          <div
            className={styles.scrollWrapper}
            ref={scrollWrapperRef}
            onScroll={() => syncScroll(scrollWrapperRef, stageNamesListRef)}
          >
            <div
              ref={chartRef}
              className={styles.ganttChart}
              style={{
                width: `${totalChartWidth}px`,
                '--scale': scaleFactor,
              }}
            >
              <div className={styles.timeline}>
                <div className={styles.timelineMarkersContainer}>{timelineMarkers}</div>
              </div>

              {weekendBlocks}
              {hourLines}

              {/* Zintegrowane połączenia SVG podążające na żywo za całym przesuwanym łańcuchem */}
              <GantMultiConnectionsSvg
                allPreparedStages={allPreparedStages}
                selectedZamowienieId={selectedZamowienieId}
                selectedStageUid={currentSelectedStage?._uid}
                scaleFactor={scaleFactor}
                showElementConections={showElementConections}
                showOprawaConnections={showOprawaConnections}
                showSingleConection={showSingleConection}
              />

              <GanttZoomOverlay zoom={ganttZoom} />

              {/* Linia TERAZ */}
              <div
                className={styles.nowLine}
                style={{ left: `${calculateDatePositionPx(new Date())}px` }}
              />

              {/* Linia SPEDYCJI oraz KONIEC OPRAWY (-24H) */}
              {activeSelectedShipmentDate && (
                <>
                  <div
                    className={styles.shipmentLine}
                    style={{ left: `${effectiveShipmentPx}px` }}
                  >
                    <span className={styles.shipmentLabel}>
                      SPEDYCJA ({date_time(activeSelectedShipmentDate)})
                    </span>
                  </div>

                  <div
                    className={styles.deadline24hLine}
                    style={{
                      left: `${Math.max(0, effectiveShipmentPx - 24 * 60 * scaleFactor)}px`,
                    }}
                  >
                    <span className={styles.deadline24hLabel}>KONIEC OPRAWY (-24H)</span>
                  </div>
                </>
              )}

              {/* Wiersze maszyn z klockami ułożonymi na torach */}
              {processorIds.map((processorId) => {
                const lanes = laneCountsByProcessor[processorId] || 1;
                const rowHeight = lanes * LANE_HEIGHT + ROW_PADDING_Y * 2;
                const stagesInRow = preparedGroupedStages[processorId] || [];

                const isAllowedDropRow =
                  activeDragUi &&
                  activeDragUi.allowedProcessorIds.includes(Number(processorId));
                const isTargetDropRow =
                  activeDragUi &&
                  Number(activeDragUi.targetProcessorId) === Number(processorId);
                const isInvalidHoverRow =
                  activeDragUi &&
                  activeDragUi.isInvalidProcessorHover &&
                  Number(activeDragUi.hoveredProcessorId) === Number(processorId);

                return (
                  <div
                    key={`right-proc-${processorId}`}
                    className={styles.processorRowRight}
                    style={{
                      height: `${rowHeight}px`,
                      backgroundColor: isInvalidHoverRow
                        ? 'rgba(239, 68, 68, 0.08)'
                        : isTargetDropRow
                        ? 'rgba(16, 185, 129, 0.10)'
                        : isAllowedDropRow
                        ? 'rgba(56, 189, 248, 0.04)'
                        : undefined,
                      outline: isTargetDropRow
                        ? '1px dashed rgba(16, 185, 129, 0.7)'
                        : undefined,
                    }}
                  >
                    {/* Delikatne podświetlenie tła dla Toru 1 (ręcznie ułożone dane wejściowe i przerwy) */}
                    <div
                      style={{
                        position: 'absolute',
                        top: `${ROW_PADDING_Y}px`,
                        left: 0,
                        right: 0,
                        height: `${LANE_HEIGHT}px`,
                        backgroundColor: 'rgba(245, 158, 11, 0.05)',
                        pointerEvents: 'none',
                      }}
                    />

                    {lanes > 1 &&
                      Array.from({ length: lanes - 1 }, (_, idx) => (
                        <div
                          key={`subline-${idx}`}
                          className={styles.subLaneDivider}
                          style={{
                            top: `${ROW_PADDING_Y + (idx + 1) * LANE_HEIGHT}px`,
                            borderTop:
                              idx === 0
                                ? '1px dashed rgba(245, 158, 11, 0.55)'
                                : undefined,
                          }}
                        />
                      ))}

                    {stagesInRow.map((stage) => {
                      const zamIdStr = String(stage.zamowienie_id);
                      const unfitOrderInfo = unfitOrdersMap[zamIdStr];
                      const isStageUnfit =
                        unfitStageUids.has(stage._uid) || Boolean(unfitOrderInfo);
                      const isBeingDragged =
                        Boolean(activeDragUi) &&
                        !stage._isManualTrack &&
                        stage._uid === activeDragUi.uid;

                      return (
                        <GantMultiStageBar
                          key={stage._renderKey || stage._uid}
                          stage={stage}
                          colorFrom={colorFrom}
                          isSelected={String(selectedZamowienieId) === zamIdStr}
                          isUnfit={isStageUnfit}
                          unfitDelayText={unfitOrderInfo?.requiredDelayHuman}
                          isBeingDragged={isBeingDragged}
                          onStageMouseDown={handleStageMouseDown}
                          onStageClick={handleStageClick}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM BAR */}
      <div className={styles.chartBottom}>
        <div className={styles.bottomLeft}>
          {currentSelectedStage ? (
            <div className={styles.selectedInfo}>
              <strong>
                {currentSelectedStage.nr}_{String(currentSelectedStage.rok || '').substring(2, 4)}
              </strong>
              <span>{currentSelectedStage.firma_nazwa}</span>
              <span className={styles.selectedTitle}>
                {currentSelectedStage.tytul || currentSelectedStage.uwagi}
              </span>

              <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '6px' }}>
                Wejście: {date_time(currentSelectedStage.start)}
                {currentSelectedStage.typ_grupy != 1 && (
                  <>
                    {' '}
                    | Symulacja:{' '}
                    <strong style={{ color: '#38bdf8' }}>
                      {date_time(currentSelectedStage.poczatek_symulacji)}
                    </strong>
                  </>
                )}
              </span>

              {currentSelectedStage.typ_grupy != 1 && (
                <div className={styles.marginControls}>
                  {/* Zmiana procesora w symulacji (tylko druk [1,2,3] i falcowanie [14..21]) */}
                  <div className={styles.marginGroup}>
                    <span className={styles.marginLabel}>Procesor:</span>
                    <select
                      value={
                        currentSelectedStage.procesor_id_symulacji ??
                        currentSelectedStage.procesor_id
                      }
                      disabled={allowedProcessorsForSelected.length <= 1}
                      onChange={(e) => handleChangeSimulatedProcessor(e.target.value)}
                      title={
                        allowedProcessorsForSelected.length <= 1
                          ? 'Dla tego procesu zmiana procesora jest zablokowana'
                          : 'Zmień procesor w ramach dozwolonej grupy'
                      }
                      style={{
                        background:
                          allowedProcessorsForSelected.length <= 1
                            ? '#111827'
                            : '#1f2937',
                        color:
                          allowedProcessorsForSelected.length <= 1
                            ? '#6b7280'
                            : '#f3f4f6',
                        border: '1px solid #374151',
                        borderRadius: '4px',
                        fontSize: '11px',
                        padding: '1px 4px',
                        cursor:
                          allowedProcessorsForSelected.length <= 1
                            ? 'not-allowed'
                            : 'pointer',
                      }}
                    >
                      {allowedProcessorsForSelected.map((proc) => (
                        <option key={proc.id} value={proc.id}>
                          {proc.nazwa}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.marginGroup}>
                    <span className={styles.marginLabel}>Lewy margines : </span>
                    <button onClick={() => handleAdjustMargin('marginLeft', -30)} title="-30 min (zakładka)">
                      <Minus size={12} />
                    </button>
                    <strong className={currentSelectedStage.marginLeft < 0 ? styles.negVal : styles.posVal}>
                      {currentSelectedStage.marginLeft || 0}m
                    </strong>
                    <button onClick={() => handleAdjustMargin('marginLeft', 30)} title="+30 min (odstęp)">
                      <Plus size={12} />
                    </button>
                  </div>

                  <div className={styles.marginGroup}>
                    <span className={styles.marginLabel}>Prawy margines :</span>
                    <button onClick={() => handleAdjustMargin('marginRight', -30)} title="-30 min (zakładka)">
                      <Minus size={12} />
                    </button>
                    <strong className={currentSelectedStage.marginRight < 0 ? styles.negVal : styles.posVal}>
                      {currentSelectedStage.marginRight || 0}m
                    </strong>
                    <button onClick={() => handleAdjustMargin('marginRight', 30)} title="+30 min (odstęp)">
                      <Plus size={12} />
                    </button>
                  </div>

                  <button
                    className={styles.resetMarginBtn}
                    onClick={handleResetStageMargins}
                    title="Zeruj marginesy tego klocka (na styk = 0)"
                  >
                    <RotateCcw size={13} /> 0m
                  </button>
                </div>
              )}
            </div>
          ) : (
            <span className={styles.hintText}>
              Kliknij klocek, aby zaznaczyć zlecenie. Przeciągnij na torze symulacji (Tory 2+), aby przesunąć etap wraz z poprzednikami lub zmienić maszynę.
            </span>
          )}
        </div>

        <div className={styles.bottomRight}>
          {delaySuggestions.length > 0 && (
            <button
              className={styles.toolBtn}
              onClick={() => setShowDelayPanel((prev) => !prev)}
              style={{ backgroundColor: '#dc2626', borderColor: '#f87171', fontWeight: 700 }}
            >
              Kolizje dat ({delaySuggestions.length})
            </button>
          )}

          <button
            className={styles.toolBtn}
            onClick={handleFitOrderToScreen}
            disabled={!selectedZamowienieId}
            title="Dopasuj zaznaczone zamówienie do ekranu"
          >
            <Shrink size={16} />
          </button>

          <button className={styles.toolBtn} onClick={handleScrollToNow}>
            Teraz
          </button>

          {[0.05, 0.1, 0.15, 0.25, 0.5, 1.0].map((val) => (
            <button
              key={val}
              className={`${styles.scaleBtn} ${scaleFactor === val ? styles.scaleBtnActive : ''}`}
              onClick={() => setScaleFactor(val)}
            >
              {val}x
            </button>
          ))}
        </div>
      </div>

      {showDelayPanel && delaySuggestions.length > 0 && (
        <GantMultiDelayPanel
          suggestions={delaySuggestions}
          procesory={procesory}
          onApplyProcessorChange={handleApplyProcessorChangeFromPanel}
          onApplyShipmentDelay={handleApplyShipmentDelay}
          onApplyProdStartShift={handleApplyProdStartShift}
          onSelectOrder={handleStageClick}
          onClearResolution={handleClearAllResolutions}
          onClose={() => setShowDelayPanel(false)}
        />
      )}
    </div>
  );
};

export default GantMulti;